import { Button, Col, Flex, Row, Space, Table, TableColumnsType, TableProps, Tabs } from 'antd';
import Card from 'antd/es/card/Card';
import dayjs from "dayjs";
import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { getPayment835FileDetails, savePayment835FileNote, downloadPayment835File, getPayment835UploadedFiles, getPayment835FileAuditHistory } from '../../../service/httpsCalls';
import "../billing.scss";
import { SorterResult } from 'antd/es/table/interface';
import { DownloadPayment835FileColumns, Payment835AuditTableColumns } from '../tableConfig';
import { LoaderSpinner } from '../../../components/common/Loader';
import TextArea from 'antd/es/input/TextArea';
import { DownloadOutlined } from '@ant-design/icons';
const { TabPane } = Tabs;

interface FileInformationFields {
    clientId: number,
    client: any | null,
    payment835FileName: string,
    totalAmount: string | null,
    totalCount: string | null,
    processedAmount: string | null,
    processedCount: string | null,
    uploadedOn: string | any,
    uploadedBy: string | null,
    status: string | null,
    currentNote: string,
    fileSize: string | null,
    latestProcessingDuration: string | null
}

interface ActionPermissions {
    canAddNote: boolean,
    canDelete: boolean,
    canDownloadFiles: boolean
}

interface PaginationObject {
    totalPages: number,
    loading: boolean,
    pageSize: number,
    currentPage: number,
    sortInfo: SorterResult<any>,
    menuVisible: boolean
}

export type Payment835FileChildRef = {
    getFileInformation: (arg?: string) => void;
};

const Payment835FileDetails = forwardRef<Payment835FileChildRef>(({ parentCallback, modalData }: any, ref) => {

    const [fileInformation, setFileInformation] = useState<FileInformationFields>({
        clientId: 0,
        client: null,
        payment835FileName: '',
        totalAmount: null,
        totalCount: null,
        processedAmount: null,
        processedCount: null,
        uploadedOn: null,
        uploadedBy: null,
        status: null,
        currentNote: '',
        fileSize: null,
        latestProcessingDuration: null
    });

    let paginationObject: PaginationObject = {
        totalPages: 0,
        loading: false,
        pageSize: 5,
        currentPage: 1,
        sortInfo: {},
        menuVisible: false
    }

    const [globalAPICalled, setGlobalAPICalled] = useState(false);
    const [editPermissions, setEditPermissions] = useState<ActionPermissions>({
        canAddNote: false,
        canDelete: false,
        canDownloadFiles: false
    });
    const [downloadFiles, setDownloadFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loader, setLoader] = useState({
        note: false
    });

    const [noteForm, setNoteForm] = useState({
        note: '',
        visible: false,
    });

    function showNoteForm() {
        setNoteForm({
            ...noteForm,
            visible: true
        });
    }

    const [auditHistory, setAuditHistory] = useState([]);
    const [auditTable, setAuditTable] = useState(paginationObject);

    const auditColumns = Payment835AuditTableColumns.map(col =>
        col.dataIndex === 'oldValue' || col.dataIndex === 'newValue' || col.dataIndex === 'note'
            ? {
                ...col,
                onCell: (
                    () => ({ style: { overflowWrap: 'anywhere' } })
                )
            }
            : col
    );

    const [disableFilterSort, setDisableFilterSort] = useState(false);

    const [downloadFileTable, setDownloadFileTable] = useState(paginationObject);

    const downloadFileColumnsCopy = DownloadPayment835FileColumns.concat([
        {
            title: 'Download',
            dataIndex: 'download',
            render: (text, record) =>
                <Button onClick={() => downloadUploadedFile(record)} loading={record.downloading} style={{ backgroundColor: '#ffffff', borderColor: '#d9d9d9', color: 'rgba(0, 0, 0, 0.88)' }} type="primary">
                    <DownloadOutlined />
                </Button>

        }
    ])

    const [downloadFileColumns, setDownloadFileColumns] = useState<TableColumnsType<any>>(downloadFileColumnsCopy);

    useEffect(() => {
        if (modalData) {
            getFileInformation()
            getAuditHistory();
            getUploadedFiles();
        }
    }, [modalData])

    function getFileInformation() {
        setLoading(true);
        getPayment835FileDetails(modalData.data.id, (res: any) => {
            setLoading(false);
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Something went wrong!!!';
                parentCallback({
                    isInvalid: true,
                    errorMessage: errorMsg
                })
            } else {
                setFileInformation({
                    clientId: res.info.clientId,
                    client: res.info.clientName,
                    payment835FileName: res.info.fileName,
                    totalAmount: res.info.totalAmount ? res.info.totalAmount.toString() : null,
                    totalCount: res.info.totalCount ? res.info.totalCount.toString() : null,
                    processedAmount: res.info.processedAmount ? res.info.processedAmount.toString() : null,
                    processedCount: res.info.processedCount ? res.info.processedCount.toString() : null,
                    uploadedOn: dayjs(res.info.uploadedOn),
                    uploadedBy: res.info.uploadedBy.toString(),
                    status: res.info.status,
                    currentNote: res.info.note,
                    fileSize: res.info.fileSize,
                    latestProcessingDuration: res.info.latestProcessingDuration
                });
                setNoteForm({ ...noteForm, note: res.info.note });
                setEditPermissions({
                    canAddNote: res.actions.canAddNote,
                    canDelete: res.actions.canDelete,
                    canDownloadFiles: res.actions.canDownloadFiles
                });
            }
        })
    }

    useImperativeHandle(ref, () => ({
        getFileInformation,
    }));

    function getAuditHistory() {
        setAuditTable({
            ...auditTable,
            loading: true
        })
        setAuditHistory([]);
        getPayment835FileAuditHistory({
            pageIndex: auditTable.currentPage,
            pageSize: auditTable?.pageSize,
            sortField: auditTable.sortInfo.order ? auditTable.sortInfo.field : null,
            sortDescending: auditTable.sortInfo.order == 'descend',
            id: modalData?.data.id
        }, (res: any) => {
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Something went wrong!!!';
                parentCallback({
                    isInvalid: true,
                    errorMessage: errorMsg
                })
                setAuditTable({
                    ...auditTable,
                    loading: false,
                    totalPages: 1
                });
            } else {
                setAuditHistory(res.items);
                setAuditTable({
                    ...auditTable,
                    loading: false,
                    totalPages: Math.ceil(res.totalRecords / (auditTable.pageSize / auditTable.pageSize))
                })
            }
        })
    }

    function downloadFile(e: any, res: any) {
        // Create a Blob URL
        const blob = new Blob([res.data], { type: res.headers["content-type"] });
        const url = window.URL.createObjectURL(blob);

        // Create a link to trigger the download
        const link = document.createElement("a");
        link.href = url;
        const fileName = e?.fileName ?? "file.txt";// Change the filename and extension as needed
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();

        // Cleanup
        link?.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    useEffect(() => {
        if (!globalAPICalled && modalData?.data.id) {
            setGlobalAPICalled(true);
        }
    });

    useEffect(() => {
        if (globalAPICalled) {
            getAuditHistory();
            getFileInformation();
            getUploadedFiles();
        }
    }, [globalAPICalled]);

    useEffect(() => {
        if (disableFilterSort) {
            const updatedCols = downloadFileColumnsCopy.map((item) => { return { ...item, sorter: false } });
            setDownloadFileColumns(updatedCols);
        } else {
            setDownloadFileColumns(downloadFileColumnsCopy);
        }
    }, [disableFilterSort]);

    useEffect(() => {
        if (globalAPICalled && modalData?.data.id && auditTable.pageSize != 0)
            getAuditHistory();
    }, [auditTable.currentPage, auditTable.pageSize, auditTable.sortInfo]);

    const auditTableChanges: TableProps<any>['onChange'] = (pagination, filters, sorter, extra) => {
        setAuditTable({
            ...auditTable,
            currentPage: pagination.current ?? 1,
            pageSize: pagination.pageSize ?? 10,
            totalPages: pagination.total ?? 100,
            sortInfo: sorter as SorterResult<any>
        })
    }

    useEffect(() => {
        if (globalAPICalled && modalData?.data.id && downloadFileTable.pageSize != 0)
            getUploadedFiles();
    }, [downloadFileTable.currentPage, downloadFileTable.pageSize, downloadFileTable.sortInfo]);

    const downloadFileTableChanges: TableProps<any>['onChange'] = (pagination, filters, sorter, extra) => {
        setDownloadFileTable({
            ...downloadFileTable,
            currentPage: pagination.current ?? 1,
            pageSize: pagination.pageSize ?? 10,
            totalPages: pagination.total ?? 100,
            sortInfo: sorter as SorterResult<any>
        })
    }

    function saveNote() {
        setLoader({
            ...loader,
            note: true
        });

        savePayment835FileNote({
            id: modalData?.data.id,
            note: noteForm.note
        }, (res: any) => {
            setLoader({
                ...loader,
                note: false
            })
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Something went wrong!!!'
                parentCallback({
                    isInvalid: true,
                    errorMessage: errorMsg
                });
            } else {
                getAuditHistory();
                setNoteForm({ ...noteForm, visible: false });

                setFileInformation({
                    ...fileInformation,
                    currentNote: noteForm.note
                });
                parentCallback({
                    isInvalid: false,
                    showMessage: true,
                    message: "Note saved successfully!"
                });
            }
        })
    }

    function getUploadedFiles() {
        setDownloadFileTable({
            ...downloadFileTable,
            loading: true
        });

        getPayment835UploadedFiles({
            pageIndex: downloadFileTable.currentPage,
            pageSize: downloadFileTable?.pageSize,
            sortField: downloadFileTable.sortInfo.order ? downloadFileTable.sortInfo.field : null,
            sortDescending: downloadFileTable.sortInfo.order == 'descend',
            id: modalData?.data.id
        }, (res: any) => {
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Something went wrong!!!';
                parentCallback({
                    isInvalid: true,
                    errorMessage: errorMsg
                })
                setDownloadFileTable({
                    ...downloadFileTable,
                    loading: false,
                    totalPages: 1
                });
            } else {
                setDownloadFiles(res.items);
                setDownloadFileTable({
                    ...downloadFileTable,
                    loading: false,
                    totalPages: Math.ceil(res.totalRecords / (downloadFileTable.pageSize / downloadFileTable.pageSize))
                })
            }
        })
    }

    function downloadUploadedFile(e: any) {
        setDownloadFiles((x: any) => x.map((rec: any) => rec.fileName == e.fileName && rec.fileType == e.fileType ? { ...e, downloading: true } : rec));

        downloadPayment835File({
            id: modalData?.data.id,
            fileName: e.fileName,
            fileType: e.fileExtension,
            filePath: e.filePath
        }, (res: any) => {
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Something went wrong!!!'
                parentCallback({
                    isInvalid: true,
                    errorMessage: errorMsg
                });
            } else {
                downloadFile(e, res);
                parentCallback({
                    isInvalid: false,
                    showMessage: true,
                    message: "File downloaded!!"
                });
            }
            setDisableFilterSort(downloadFiles?.filter((x: any) => x.downloading == true).length > 1);
            setTimeout(() => {
                setDownloadFiles((x: any) => x.map((rec: any) => rec.fileName == e.fileName && rec.fileType == e.fileType ? { ...e, downloading: false } : rec));
            }, 500)
        })
    }


    const getDynamicSpan = (text: string): number => {
        if (!text) return 4; // default
        if (text.length > 50) return 12;
        if (text.length > 30) return 8;
        return 4;
    };


    return (

        <div style={{ paddingTop: '15px', backgroundColor: 'none', }}>
            {editPermissions && fileInformation && fileInformation.client ? <>
                <Card styles={{ body: { padding: '15px' } }} loading={loading}>
                    <p
                        style={{
                            fontSize: 14,
                            color: 'rgba(0, 0, 0, 0.85)',
                            marginTop: '0px',
                            fontWeight: 500,
                        }}
                    >
                        File Information
                    </p>
                    <div>
                        <Row style={{ paddingBottom: '1rem', background: '#fff' }}>
                            <Col span={getDynamicSpan(fileInformation.payment835FileName)} style={{ paddingRight: '0.5rem', overflowWrap: 'anywhere' }}>
                                <label>835 Payment File Name</label>
                                <br />
                                {fileInformation.payment835FileName}
                            </Col>
                            <Col span={getDynamicSpan(fileInformation.client)} style={{ paddingRight: '0.5rem', overflowWrap: 'anywhere' }}>
                                <label>Client</label>
                                <br />
                                {fileInformation.client}
                            </Col>
                            <Col span={4} style={{ paddingRight: '0.5rem' }}>
                                <label>Status</label>
                                <br />
                                {fileInformation.status}
                            </Col>
                            {fileInformation.fileSize && (
                            <Col span={4} style={{ paddingRight: '0.5rem' }}>
                                <label>File Size</label>
                                <br />
                                {fileInformation.fileSize}
                                </Col>
                            )}
                        </Row>
                        <Row style={{ paddingBottom: '1rem', background: '#fff' }}>
                            <Col span={4} style={{ paddingRight: '0.5rem' }}>
                                <label>Uploaded By</label>
                                <br />
                                {fileInformation.uploadedBy}
                            </Col>
                            <Col span={4} style={{ paddingRight: '0.5rem' }}>
                                <label>Uploaded On</label>
                                <br />
                                {fileInformation.uploadedOn ? dayjs(fileInformation.uploadedOn).format('MM/DD/YYYY') : ''}
                            </Col>
                            {fileInformation.totalCount && (
                                <Col span={4} style={{ paddingRight: '0.5rem' }}>
                                    <label>Total Count</label>
                                    <br />
                                    {fileInformation.totalCount}
                                </Col>
                            )}
                            {fileInformation.totalAmount && (
                                <Col span={4} style={{ paddingRight: '0.5rem' }}>
                                    <label>Total Amount</label>
                                    <br />
                                    {fileInformation.totalAmount}
                                </Col>
                            )}
                            {fileInformation.processedCount && (
                                <Col span={4} style={{ paddingRight: '0.5rem' }}>
                                    <label>Processed Count</label>
                                    <br />
                                    {fileInformation.processedCount}
                                </Col>
                            )}
                            {fileInformation.processedAmount && (
                                <Col span={4} style={{ paddingRight: '0.5rem' }}>
                                    <label>Processed Amount</label>
                                    <br />
                                    {fileInformation.processedAmount}
                                </Col>
                            )}
                        </Row>
                        <Row style={{ paddingBottom: '1rem', background: '#fff' }}>
                            {fileInformation.latestProcessingDuration && (
                                <Col span={4} style={{ paddingRight: '0.5rem', overflowWrap: 'anywhere' }}>
                                    <label>Latest Processing Duration</label>
                                    <br />
                                    {fileInformation.latestProcessingDuration}
                                </Col>
                            )}
                        </Row>
                        <Flex justify="end">
                            <Space>
                                {
                                    editPermissions?.canAddNote &&
                                    <Button key="saveNoteBtn" disabled={noteForm.visible} onClick={e => showNoteForm()} style={{ backgroundColor: "blue", color: "#fff" }}>Add Note</Button>
                                }
                            </Space>
                        </Flex>
                    </div>
                </Card>
                {
                    <div style={{ padding: "1rem", background: '#eff1f5' }}></div>
                }
                {
                    editPermissions?.canAddNote && noteForm.visible &&
                    <div style={{ background: "#eff1f5" }}>
                        <Card styles={{ body: { padding: '15px' } }}>
                            <p
                                style={{
                                    fontSize: 14,
                                    color: 'rgba(0, 0, 0, 0.85)',
                                    marginTop: '0px',
                                    fontWeight: 500,
                                }}
                            >
                                Note
                            </p>
                            <Row style={{ paddingBottom: '0.5rem' }}>
                                <Col span={24}>
                                    <TextArea
                                        placeholder="Enter Here....."
                                        autoSize={{ minRows: 2, maxRows: 6 }}
                                        value={noteForm.note}
                                        onChange={(e: any) => setNoteForm({ ...noteForm, note: e.target.value })}
                                    />
                                </Col>
                            </Row>
                            <Flex justify="end">
                                <Space>
                                    <Button key="saveNote" disabled={noteForm.note === fileInformation.currentNote} loading={loader.note} onClick={e => saveNote()} style={{ backgroundColor: "blue", color: "#fff" }}>Save</Button>
                                    <Button key="cancelNote" onClick={e => setNoteForm({ note: fileInformation.currentNote, visible: false })} style={{ backgroundColor: '#ffffff', borderColor: '#d9d9d9', color: 'rgba(0, 0, 0, 0.88)' }}>Cancel</Button>
                                </Space>
                            </Flex>
                        </Card>
                        <div style={{ padding: "1rem" }}></div>
                    </div>
                }
                <Flex style={{ padding: "1rem", background: '#fff' }}>
                    <Tabs type="card" style={{ width: '100%' }}>
                        <TabPane tab="Audit History" key="auditHistoryTab">
                            <Table style={{ overflowY: "auto", height: "auto" }}
                                dataSource={auditHistory}
                                pagination={{
                                    defaultCurrent: 1,
                                    current: auditTable.currentPage,
                                    pageSize: auditTable.pageSize,
                                    total: auditTable.totalPages,
                                    showTotal: (total, range) => `${range[0] || 0}-${range[1] || 1} of ${total} items`,
                                    showSizeChanger: true,
                                }}
                                columns={auditColumns}
                                onChange={auditTableChanges}
                                rowKey="id"
                                loading={{ indicator: <LoaderSpinner size={24} topMargin="10px" />, spinning: auditTable.loading }}
                                onHeaderRow={() => {
                                    return {
                                        onContextMenu: (event) => {
                                            event.preventDefault();
                                            if (!auditTable.menuVisible) {
                                                document.addEventListener(`click`, function onClickOutside() {
                                                    setAuditTable({
                                                        ...auditTable,
                                                        menuVisible: false
                                                    });
                                                    document.removeEventListener(`click`, onClickOutside);
                                                });
                                            }
                                            setAuditTable({
                                                ...auditTable,
                                                menuVisible: true
                                            });
                                        }
                                    };
                                }}
                            />
                        </TabPane>
                        <TabPane tab="Download File" key="downloadTab">
                            <Table style={{ overflowY: "auto", height: "auto" }}
                                dataSource={downloadFiles}
                                pagination={{
                                    defaultCurrent: 1,
                                    current: downloadFileTable.currentPage,
                                    pageSize: downloadFileTable.pageSize,
                                    total: downloadFileTable.totalPages,
                                    showTotal: (total, range) => `${range[0] || 0}-${range[1] || 1} of ${total} items`,
                                    showSizeChanger: true,
                                    disabled: disableFilterSort
                                }}
                                columns={downloadFileColumns}
                                onChange={downloadFileTableChanges}
                                rowKey="fileName"
                                loading={{ indicator: <LoaderSpinner size={24} topMargin="10px" />, spinning: downloadFileTable.loading }}
                                onHeaderRow={(columns, index) => {
                                    return {
                                        onContextMenu: (event) => {
                                            event.preventDefault();
                                            if (!downloadFileTable.menuVisible) {
                                                document.addEventListener(`click`, function onClickOutside() {
                                                    setDownloadFileTable({
                                                        ...downloadFileTable,
                                                        menuVisible: false
                                                    });
                                                    document.removeEventListener(`click`, onClickOutside);
                                                });
                                            }
                                            setDownloadFileTable({
                                                ...downloadFileTable,
                                                menuVisible: true
                                            });
                                        }
                                    };
                                }}
                            />
                        </TabPane>
                    </Tabs>
                </Flex>
            </> :
                <>   <Card styles={{ body: { padding: '15px' } }} loading={loading}></Card></>
            }

        </div>
    )
})

export default Payment835FileDetails;